import PolicyLayout, { Section } from "@/components/PolicyLayout";
import { BUSINESS, addressOneLine } from "@/data/business";

export default function TermsOfService() {
  const lastUpdated = "February 1, 2026";
  return (
    <PolicyLayout
      eyebrow="Terms"
      title="Terms of Service"
      intro="The agreement between you and us when you use the site or buy a product. Short, clear, no surprises."
      testId="terms-page"
    >
      <p className="text-sm text-muted-foreground -mt-4">Last updated: {lastUpdated}</p>

      <Section title="1. Acceptance">
        <p>
          By accessing {BUSINESS.url} (the "Site") or placing an order, you agree to these Terms of
          Service. If you don't agree, please don't use the Site.
        </p>
      </Section>

      <Section title="2. Who we are">
        <p>
          The Site is operated by {BUSINESS.legalName}, located at {addressOneLine()}. Contact:{" "}
          <a href={`mailto:${BUSINESS.email.support}`} className="text-primary font-semibold">{BUSINESS.email.support}</a>.
        </p>
      </Section>

      <Section title="3. Products & pricing">
        <p>
          Product descriptions, photos, and prices are provided in good faith and may change at any
          time without notice. We do our best to display accurate colors, sizes, and descriptions,
          but minor variation between the image and the physical product can occur. All prices are
          in US dollars unless otherwise noted.
        </p>
        <p>
          We reserve the right to refuse or cancel any order at our discretion (e.g., suspected
          fraud, pricing errors, or out-of-stock items). If we cancel an order you've already paid
          for, we'll issue a full refund.
        </p>
      </Section>

      <Section title="4. Orders & payment">
        <p>
          Placing an order is an offer to buy; the contract is formed when we send you the order
          confirmation email. Payment is processed by Stripe — by checking out you agree to Stripe's{" "}
          <a href="https://stripe.com/legal/end-users" className="text-primary font-semibold" target="_blank" rel="noopener noreferrer">Services Agreement</a>.
          We never store your card number.
        </p>
      </Section>

      <Section title="5. Shipping">
        <p>
          See our <a href="/shipping-policy" className="text-primary font-semibold">Shipping Policy</a> for handling times,
          transit times, and costs. Risk of loss passes to you when the carrier marks the package
          as delivered.
        </p>
      </Section>

      <Section title="6. Returns & refunds">
        <p>
          We offer a 30-day return window. Full details on what's eligible and how to start a return
          are on our <a href="/return-policy" className="text-primary font-semibold">Return Policy</a> page.
        </p>
      </Section>

      <Section title="7. Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li>Use the Site for any unlawful purpose or to violate any laws in your jurisdiction</li>
          <li>Attempt to gain unauthorised access to our systems</li>
          <li>Scrape, crawl, or harvest data beyond ordinary browsing</li>
          <li>Reproduce, duplicate, or resell content from the Site without our written permission</li>
          <li>Use the Site to transmit malware or otherwise interfere with normal operation</li>
        </ul>
      </Section>

      <Section title="8. Intellectual property">
        <p>
          All content on the Site — including product photos, copy, logos, and the "Pawventure
          Essentials" name — is owned by {BUSINESS.legalName} or licensed to us. You may not
          reproduce or redistribute it without written permission. The squeaky rugby ball design is
          a generic dog toy form factor; we claim no exclusivity over the product type itself.
        </p>
      </Section>

      <Section title="9. Reviews & user content">
        <p>
          If you submit a review, photo, or comment, you grant us a non-exclusive, royalty-free,
          worldwide licence to use it for marketing and promotional purposes. You're responsible for
          ensuring you have the rights to anything you submit.
        </p>
      </Section>

      <Section title="10. Warranties & disclaimers">
        <p>
          Our products are intended for use as dog toys with appropriate supervision. We do not
          guarantee the toy is indestructible — no dog toy is. Please pick the right size for your
          dog and discard the toy if it becomes badly damaged.
        </p>
        <p>
          THE SITE AND PRODUCTS ARE PROVIDED "AS IS" TO THE FULLEST EXTENT PERMITTED BY LAW. WE
          DISCLAIM ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR
          PURPOSE. NOTHING IN THESE TERMS LIMITS YOUR STATUTORY CONSUMER RIGHTS UNDER THE LAW OF
          YOUR JURISDICTION.
        </p>
      </Section>

      <Section title="11. Limitation of liability">
        <p>
          To the maximum extent permitted by law, our total liability arising from any single order
          is limited to the amount you paid for that order. We are not liable for indirect,
          consequential, or punitive damages.
        </p>
      </Section>

      <Section title="12. Indemnification">
        <p>
          You agree to indemnify and hold {BUSINESS.legalName} harmless from any claim arising out
          of your breach of these Terms or your misuse of the Site or products.
        </p>
      </Section>

      <Section title="13. Governing law & disputes">
        <p>
          These Terms are governed by the laws of {BUSINESS.jurisdiction}, without regard to
          conflict-of-laws principles. Any dispute will be resolved in the courts of that
          jurisdiction, unless mandatory consumer protection rules in your country give you the
          right to bring proceedings locally.
        </p>
      </Section>

      <Section title="14. Changes">
        <p>
          We may update these Terms occasionally. The "Last updated" date will reflect any change.
          Continued use of the Site after updates counts as acceptance.
        </p>
      </Section>

      <Section title="15. Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href={`mailto:${BUSINESS.email.legal}`} className="text-primary font-semibold">{BUSINESS.email.legal}</a>.
        </p>
      </Section>
    </PolicyLayout>
  );
}
